import type AutocompleteElement from '@github/auto-complete-element'
import type SeveritySelectionElement from '../../../../components/advisories/severity-selection-element'
import type SeveritySelectionNextElement from '../../../../components/advisories/severity-selection-next-element'
import type {SimpleResponse} from '@github/remote-form'
import {handleCommentCancelButtonClick} from '../behaviors/commenting/edit'
import {hideFormGroupError} from './edit'
// eslint-disable-next-line no-restricted-imports
import {on} from 'delegated-events'
import {remoteForm} from '@github/remote-form'
import {requestSubmit} from '@github-ui/form-utils'

on(
  'details-menu-selected',
  '.js-advisory-menu',
  function (event) {
    const menuItem = event.detail.relatedTarget
    const classToShow = menuItem.getAttribute('data-advisory-info-show')!
    const elementsToToggle = document.querySelectorAll<HTMLElement>(
      '.js-show-if-publishing, .js-show-if-requesting-cve',
    )

    for (const element of elementsToToggle) {
      element.hidden = !element.classList.contains(classToShow)
    }
  },
  {capture: true},
)

function toggleListVisibility(list: HTMLElement) {
  const credits = list.querySelectorAll<HTMLElement>('.js-advisory-credit-row')
  const listIsEmpty = Array.from(credits).every(credit => credit.hidden)
  list.hidden = listIsEmpty
}

function addCredit(container: HTMLElement, response: SimpleResponse) {
  const credit = response.html.querySelector<HTMLElement>('.js-advisory-credit-row')!
  const recipientId = credit.getAttribute('data-recipient-id')!
  const list = container.querySelector<HTMLElement>('.js-advisory-credits-list')!
  const existingCredit = list.querySelector<HTMLElement>(`.js-advisory-credit-row[data-recipient-id='${recipientId}']`)

  if (existingCredit) {
    const destroyField = existingCredit.querySelector<HTMLInputElement>('.js-advisory-credit-destroy-field')!

    if (!existingCredit.hidden) {
      existingCredit.classList.add('user-already-added')
    }

    destroyField.value = 'false'
    existingCredit.hidden = false
  } else {
    const nextIndexField = container.querySelector<HTMLInputElement>('.js-next-credit-index-field')!

    list.append(credit)
    nextIndexField.value = String(Number(nextIndexField.value) + 1)
  }

  toggleListVisibility(list)
}

function removeCredit(credit: HTMLElement) {
  const destroyField = credit.querySelector<HTMLInputElement>('.js-advisory-credit-destroy-field')!
  const list = credit.closest<HTMLElement>('.js-advisory-credits-list')!

  destroyField.value = 'true'
  credit.hidden = true
  credit.classList.remove('user-already-added')

  toggleListVisibility(list)
}

remoteForm('.js-repository-advisory-add-credit-form', async function (form, wants) {
  const container = form.closest<HTMLElement>('.js-repository-advisory-form-container')!

  form.reset()

  const response = await wants.html()
  if (!response) return

  addCredit(container, response)
})

on('auto-complete-change', '.js-add-credit-auto-complete', function (event) {
  const autoComplete = event.target as AutocompleteElement
  const container = autoComplete.closest<HTMLElement>('.js-repository-advisory-form-container')!
  const form = container.querySelector<HTMLFormElement>('.js-repository-advisory-add-credit-form')!

  requestSubmit(form)
})

on('click', '.js-advisory-credit-remove-button', function (event) {
  const credit = event.currentTarget.closest<HTMLElement>('.js-advisory-credit-row')!

  removeCredit(credit)
})

on('click', '.js-repository-advisory-credit-popover-edit-button', function (event) {
  const button: HTMLButtonElement = event.target as HTMLButtonElement
  if (!button) return
  const form = button.closest<HTMLFormElement>('form')!
  requestSubmit(form)
})

function toggleRepositoryAdvisoryEditForm(shown: boolean) {
  const detailElements = document.querySelectorAll<HTMLElement>('.js-repository-advisory-details')
  for (const detailElement of detailElements) {
    detailElement.hidden = shown
  }

  const editFormContainer = document.querySelector<HTMLElement>('.js-repository-advisory-edit-form-container')
  if (editFormContainer) {
    editFormContainer.hidden = !shown
  }
}

on('click', '.js-repository-advisory-edit-button', function () {
  toggleRepositoryAdvisoryEditForm(true)
})

on('change', '.js-bypass-branch-protections', function ({currentTarget}) {
  const mergeMessageContainer = currentTarget.closest<HTMLElement>('.merge-message')!
  const newStateValue = (currentTarget as HTMLInputElement).checked
  const mergeButtons = mergeMessageContainer.querySelectorAll<HTMLButtonElement>('.merge-button')

  for (const button of mergeButtons) {
    button.disabled = !newStateValue
  }
})

// We can remove this method call once the advisory_db_cvss_v4 feature flag is removed
on('click', '.js-repository-advisory-edit-form-cancel-button', function (event) {
  // Check whether the user wants to give up editing.
  // Form values revert to their previous default if they agree.
  if (!handleCommentCancelButtonClick(event)) {
    return
  }
  const severitySelectionElement = document.querySelector<SeveritySelectionElement>('severity-selection')
  if (severitySelectionElement) {
    // Select must be manually revert backed to its original value
    severitySelectionElement.resetSeveritySelectElement()

    // Reselect the calculator controls and remove lingering errors.
    // Necessary because the reversal of form values above is programmatic
    // and does not trigger change events.
    const {severitySelectElement} = severitySelectionElement
    severitySelectionElement.handleVectorStringInput()
    severitySelectionElement.handleVectorStringBlur()
    severitySelectionElement.toggleSeverityCalculator(severitySelectElement.value)
  }

  // Actually close the form
  toggleRepositoryAdvisoryEditForm(false)
})

on('click', '.js-repository-advisory-edit-form-cancel-button-next', function (event) {
  // Check whether or not the user wants to give up editing.
  // Form values revert to their previous default if they agree.
  if (!handleCommentCancelButtonClick(event)) {
    return
  }

  const severitySelectionNextElement = document.querySelector<SeveritySelectionNextElement>('severity-selection-next')

  if (severitySelectionNextElement) {
    // The "Severity" drop-down must be manually reverted back to its original value
    severitySelectionNextElement.resetSeveritySelectElement()

    // Re-select the original calculator controls and remove any lingering errors.  This is necessary because the
    // reversal of form values above is programmatic and does not trigger change events.
    // TODO:  Do we still to call `handleVectorStringInput()` and `handleVectorStringBlur()`?
    severitySelectionNextElement.handleVectorStringInput()
    severitySelectionNextElement.handleVectorStringBlur()
    severitySelectionNextElement.toggleSeverityCalculator(severitySelectionNextElement.severitySelect.value)
  }

  // Actually close the form
  toggleRepositoryAdvisoryEditForm(false)
})

on('change', '.js-cve-selection', function (event) {
  const selection = event.currentTarget as HTMLSelectElement
  selection.form!.querySelector<HTMLElement>('.js-existing-cve')!.hidden = selection.value === 'requesting'
})
on('focusout', '.js-existing-cve', hideFormGroupError)
